import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API, Auth, Storage } from 'aws-amplify';
import { AppDispatch, RootState } from '../store';
import { BucketImage } from '../../hooks/useBucketImage';
import { LearnMoreButtonConfig, LearnMoreSettings } from '../../constants/partnerBranding';
import { createRequestStatusObject, RequestStatus, RequestStatusObject } from '../utils';

// Public, pre-login branding fetched via the org slug in the `?org=` query param.
export interface PartnerBranding {
	organisationId: string;
	displayName?: string | null;
	logoImage?: BucketImage | null;
	logoImageDark?: BucketImage | null;
	// Pre-signed logo URLs generated server-side so unauthenticated visitors can
	// load the logo without needing S3 read access via the guest Cognito role.
	// `logoUrl` is the light-theme variant; `logoUrlDark` the dark-theme variant.
	logoUrl?: string | null;
	logoUrlDark?: string | null;
	// The organisation's "Learn more" button; null when switched off.
	learnMore?: LearnMoreButtonConfig | null;
}

interface FetchPartnerBrandingResult {
	slug: string;
	branding: PartnerBranding | null;
}

/**
 * Post-sign-up personalization derived from the constituent roster the new user
 * matched on. Used to pre-populate the profile and to surface the partner's
 * licensed product first during onboarding. Always optional — a null value simply
 * means "no roster match", which is the normal case for self-serve sign-ups.
 */
export interface PartnerPersonalization {
	organisationId: string;
	displayName?: string | null;
	licenseProductId?: string | null;
	rosterName?: string | null;
}

/** An organisation's own branding record, as its admins edit it on the Branding page. */
export interface OrgBranding {
	urlSlug?: string;
	displayName?: string | null;
	logoImage?: BucketImage | null;
	logoImageDark?: BucketImage | null;
	learnMore?: LearnMoreSettings | null;
}

/**
 * What a rejected branding write carries. A thrown error is serialised down to
 * name/message/stack, losing the response, so the status and code are kept.
 */
export interface BrandingApiError {
	status?: number;
	code?: string;
}

const toBrandingApiError = (error: unknown): BrandingApiError => {
	const response = (error as { response?: { status?: number; data?: { code?: string } } })?.response;
	return { status: response?.status, code: response?.data?.code };
};

/** The locale key explaining why saving branding failed. */
export const brandingSaveErrorKey = (error: BrandingApiError | undefined): string => {
	if (error?.status === 409) {
		return 'pages.organisation.branding.errors.slugConflict';
	}
	if (error?.status === 400) {
		return error.code === 'INVALID_LEARN_MORE'
			? 'pages.organisation.branding.errors.learnMoreInvalid'
			: 'pages.organisation.branding.errors.slugInvalid';
	}
	return 'pages.organisation.branding.errors.saveFailed';
};

export interface PartnerBrandingState {
	// Branding keyed by slug. Acting as the cache, this guarantees that
	// re-mounting the auth logo for a slug we've already fetched — e.g. toggling
	// the Sign In / Sign Up tab — reuses the SAME branding object, including the
	// SAME pre-signed logo URL, so the <img> src is unchanged and the browser
	// serves the image from cache with no visible reload. `null` records slugs
	// that resolved to no branding, so we don't re-request them.
	brandingBySlug: Record<string, PartnerBranding | null>;
	// The slug currently being fetched, so concurrent mounts don't double-request.
	loadingSlug: string | null;
	readRequest: RequestStatusObject;
	// `undefined` = not yet fetched, `null` = fetched and there is no roster match.
	personalization: PartnerPersonalization | null | undefined;
	// Organisations' own branding records, keyed by organisation id. `null` means
	// the organisation has not set up branding yet.
	orgBrandingById: Record<string, OrgBranding | null>;
}

const initialState: PartnerBrandingState = {
	brandingBySlug: {},
	loadingSlug: null,
	readRequest: createRequestStatusObject(),
	personalization: undefined,
	orgBrandingById: {}
};

// Warms the browser cache with the logo image so it's ready to paint the moment
// the component renders, rather than kicking off the image download on mount.
const preloadLogo = (branding: PartnerBranding | null): void => {
	if (!branding || typeof Image === 'undefined') return;
	[branding.logoUrl, branding.logoUrlDark].forEach((url) => {
		if (url) {
			const img = new Image();
			img.src = url;
		}
	});
};

/**
 * Fetches the public, pre-login branding for an organisation slug. Pre-signed
 * logo URLs are valid for 7 days (see the ssharing branding handler), so the
 * result is safe to cache for the lifetime of a login session. Resolves to
 * `null` (never rejects) when the slug has no branding or the request fails, so
 * callers simply fall back to the default logo.
 *
 * The `condition` guard makes the thunk idempotent per slug: it no-ops when the
 * slug is already resolved or in flight, so the bootstrap prefetch and the
 * on-mount fetch collapse into a single network request.
 */
export const fetchPartnerBranding = createAsyncThunk<
	FetchPartnerBrandingResult,
	string,
	{ dispatch: AppDispatch; state: RootState }
>(
	'partnerBranding/fetch',
	async (slug) => {
		const branding: PartnerBranding | null = await API.get(
			'inspirusapi',
			`/sharing/branding/${encodeURIComponent(slug)}`,
			{}
		)
			.then((result: PartnerBranding) => result ?? null)
			// No branding for this slug (or a transient error) — fall back silently.
			.catch(() => null);
		preloadLogo(branding);
		return { slug, branding };
	},
	{
		condition: (slug, { getState }) => {
			const state = getState().partnerBranding;
			if (Object.prototype.hasOwnProperty.call(state.brandingBySlug, slug)) {
				return false;
			}
			return state.loadingSlug !== slug;
		}
	}
);

/**
 * Resolves the signed-in user against their organisation's constituent roster.
 *
 * Never rejects: personalization is a nice-to-have layered on top of sign-up, so a
 * failure here must degrade to the plain, unpersonalized experience rather than
 * block the user. The optional `slug` disambiguates when someone appears on more
 * than one roster.
 */
export const fetchPartnerPersonalization = createAsyncThunk<PartnerPersonalization | null, string | undefined>(
	'partnerBranding/fetchPersonalization',
	async (slug) => {
		try {
			const response = await API.get('inspirusapi', '/organisations/me/partner-personalization', {
				headers: {},
				response: true,
				queryStringParameters: slug ? { slug } : undefined
			});
			return response?.data?.personalization ?? null;
		} catch {
			return null;
		}
	}
);

// -------------------- An organisation's own branding (Branding page) --------------------

type ThunkConfig = { dispatch: AppDispatch; state: RootState };

const orgBrandingPath = (organisationId: string) => `/organisations/${organisationId}/branding`;

/**
 * Loads an organisation's branding for its Branding page. Resolves to `null`
 * when the organisation has none yet; rejects only when the request fails.
 */
export const fetchOrganisationBranding = createAsyncThunk<
	{ organisationId: string; branding: OrgBranding | null },
	string,
	ThunkConfig
>('partnerBranding/fetchOrganisationBranding', async (organisationId, { rejectWithValue }) => {
	try {
		const result: { branding: OrgBranding | null } = await API.get(
			'inspirusapi',
			orgBrandingPath(organisationId),
			{}
		);
		return { organisationId, branding: result?.branding ?? null };
	} catch (error) {
		return rejectWithValue(toBrandingApiError(error));
	}
});

/** Saves an organisation's branding. Rejects with a BrandingApiError (see brandingSaveErrorKey). */
export const saveOrganisationBranding = createAsyncThunk<
	{ organisationId: string; branding: OrgBranding },
	{ organisationId: string; branding: OrgBranding },
	ThunkConfig
>('partnerBranding/saveOrganisationBranding', async ({ organisationId, branding }, { rejectWithValue }) => {
	try {
		const result: { branding: OrgBranding } = await API.put('inspirusapi', orgBrandingPath(organisationId), {
			body: branding
		});
		return { organisationId, branding: result?.branding ?? branding };
	} catch (error) {
		return rejectWithValue(toBrandingApiError(error));
	}
});

/**
 * Uploads a prepared logo (already resized to PNG by the page) to the public
 * bucket and returns its reference. Saving branding is what attaches it.
 */
export const uploadOrganisationBrandingLogo = createAsyncThunk<
	BucketImage,
	{ organisationId: string; variant: 'light' | 'dark'; image: Blob },
	ThunkConfig
>('partnerBranding/uploadOrganisationBrandingLogo', async ({ organisationId, variant, image }) => {
	const key = `organisations/${organisationId}/branding/logo-${variant}-${Date.now()}`;
	await Storage.put(key, image, { level: 'public', contentType: 'image/png' });
	const { identityId } = await Auth.currentCredentials();
	return { key, level: 'public', identityId };
});

const partnerBrandingSlice = createSlice({
	name: 'partnerBranding',
	initialState,
	reducers: {
		resetPartnerBranding: () => initialState
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchPartnerPersonalization.fulfilled, (state, action) => {
				state.personalization = action.payload;
			})
			.addCase(fetchOrganisationBranding.fulfilled, (state, action) => {
				state.orgBrandingById[action.payload.organisationId] = action.payload.branding;
			})
			.addCase(saveOrganisationBranding.fulfilled, (state, action) => {
				state.orgBrandingById[action.payload.organisationId] = action.payload.branding;
			})
			.addCase(fetchPartnerBranding.pending, (state, action) => {
				state.readRequest.status = RequestStatus.PENDING;
				state.readRequest.error = null;
				state.loadingSlug = action.meta.arg;
			})
			.addCase(fetchPartnerBranding.fulfilled, (state, action) => {
				state.readRequest.status = RequestStatus.SUCCESS;
				state.readRequest.error = null;
				state.brandingBySlug[action.payload.slug] = action.payload.branding;
				if (state.loadingSlug === action.payload.slug) {
					state.loadingSlug = null;
				}
			})
			.addCase(fetchPartnerBranding.rejected, (state, action) => {
				state.readRequest.status = RequestStatus.ERROR;
				state.readRequest.error = action.error;
				if (state.loadingSlug === action.meta.arg) {
					state.loadingSlug = null;
				}
			});
	}
});

export const { resetPartnerBranding } = partnerBrandingSlice.actions;

export default partnerBrandingSlice.reducer;
